import { DataTable } from "@/components/common/DataTable";
import MultiSelect, { MultiSelectOption } from "@/components/common/MultiSelect";
import { Input } from "@/components/ui/input";
import { useApiClient } from "@/context/ApiContext";
import { useStaffingTable } from "@/hooks/useStaffingTable";
import { getDistinctCities, getStaffingClientsWithFilters } from "@/services/client/Client";
import { getDistinctInsuranceCompanies } from "@/services/insurance/Insurance";
import { StaffingClient } from "@/types/common/client";
import { PAGE_SIZE } from "@/utils/constants";
import {
    buildClientCityLookupFilter,
    buildClientFilter,
    CLIENT_STATUS_OPTIONS,
    ClientFilters,
    DEFAULT_CLIENT_STATUSES,
    ENROLLMENT_STATUS_OPTIONS,
} from "@/utils/staffingUtil";
import { useEffect, useMemo, useState } from "react";
import { clientsColumns } from "../columns/StaffingDashboardColumns";

interface ClientsTabProps {
    region: string | null;
}

const DEFAULT_FILTERS: ClientFilters = {
    name: '',
    statuses: DEFAULT_CLIENT_STATUSES,
    enrollmentStatuses: [],
    insuranceCompanies: [],
    cities: [],
};

const ClientsTab = ({ region }: ClientsTabProps) => {
    const apiClient = useApiClient();
    const [insuranceOptions, setInsuranceOptions] = useState<MultiSelectOption[]>([]);
    const [cityOptions, setCityOptions] = useState<MultiSelectOption[]>([]);

    const { data, loading, error, total, filters, applyFilter, handleNameChange, handleParamsChange } =
        useStaffingTable<StaffingClient, ClientFilters>({
            region,
            defaultFilters: DEFAULT_FILTERS,
            buildFilter: buildClientFilter,
            defaultOrderBy: 'NameFL asc',
            fetcher: getStaffingClientsWithFilters,
        });

    useEffect(() => {
        if (!region) return;
        getDistinctInsuranceCompanies(region, apiClient)
            .then(res => setInsuranceOptions((res.data as { Title: string }[]).map(v => ({ label: v.Title, value: v.Title }))))
            .catch(() => {});
    }, [region, apiClient]);

    const { statuses, enrollmentStatuses, insuranceCompanies } = filters;
    const cityLookupFilters = useMemo(() => ({ name: '', statuses, enrollmentStatuses, insuranceCompanies, cities: [] }), [statuses, enrollmentStatuses, insuranceCompanies]);
    useEffect(() => {
        if (!region) return;
        getDistinctCities(buildClientCityLookupFilter(region, cityLookupFilters), apiClient)
            .then(res => setCityOptions((res.data as { City: string }[]).map(v => ({ label: v.City, value: v.City }))))
            .catch(() => {});
    }, [region, cityLookupFilters, apiClient]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <Input
                    placeholder="Search by name..."
                    value={filters.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-48"
                />
                <MultiSelect
                    options={CLIENT_STATUS_OPTIONS}
                    value={filters.statuses}
                    onChange={(statuses) => applyFilter({ ...filters, statuses })}
                    placeholder="Status"
                    searchPlaceholder="Search statuses..."
                />
                <MultiSelect
                    options={ENROLLMENT_STATUS_OPTIONS}
                    value={filters.enrollmentStatuses}
                    onChange={(enrollmentStatuses) => applyFilter({ ...filters, enrollmentStatuses })}
                    placeholder="Enrollment Status"
                    searchPlaceholder="Search enrollment statuses..."
                />
                <MultiSelect
                    options={insuranceOptions}
                    value={filters.insuranceCompanies}
                    onChange={(insuranceCompanies) => applyFilter({ ...filters, insuranceCompanies })}
                    placeholder="Insurance Company"
                    searchPlaceholder="Search insurance..."
                />
                <MultiSelect
                    options={cityOptions}
                    value={filters.cities}
                    onChange={(cities) => applyFilter({ ...filters, cities })}
                    placeholder="City"
                    searchPlaceholder="Search cities..."
                />
            </div>
            <DataTable
                data={data}
                columns={clientsColumns}
                loading={loading}
                error={error ?? undefined}
                serverSide={{ total, pageSize: PAGE_SIZE, onParamsChange: handleParamsChange }}
            />
        </div>
    );
};

export default ClientsTab;
